import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';

import { LESSON_TYPES, COURSE_STRUCTURES, COURSE_TYPES } from 'constants/common';

import * as api from 'api/methods';

import {
  createCourseModule,
  updateCourseById,
  updateDailyChallengeById,
} from 'store/course/actions';
import { currentCourseSelector } from 'store/course/selectors';

import StructureSelect from './StructureSelect';

const _getLessonBasePath = (courseType: any, courseId: any) => {
  switch (courseType) {
    case COURSE_TYPES.TRAINING: {
      return `/trainings/${courseId}/lessons`;
    }
    case COURSE_TYPES.DAILY_CHALLENGE: {
      return `/daily-challenges/${courseId}/lessons`;
    }
    case COURSE_TYPES.ARCADE: {
      return `/arcades/${courseId}/lessons`;
    }
    default: {
      return `/courses/${courseId}/lessons`;
    }
  }
};

const StructureSelectContainer = (props: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { id: courseId } = useParams();

  const course = useSelector(currentCourseSelector);

  const handleAddModuleClick = async () => {
    try {
      await dispatch(
        createCourseModule({
          courseId,
          data: { title: '', order: 1 },
        }),
      ).unwrap();

      if (course?.type === COURSE_TYPES.DAILY_CHALLENGE) {
        await dispatch(
          updateDailyChallengeById({
            courseId,
            data: { structure: COURSE_STRUCTURES.MODULES },
          }),
        ).unwrap();
      } else {
        await dispatch(
          updateCourseById({
            courseId,
            data: { structure: COURSE_STRUCTURES.MODULES },
          }),
        ).unwrap();
      }
    } catch (error) {
      console.warn(error);
    }
  };

  const handleAddLessonClick = async (contextItem: any) => {
    const nextLessonOrder = 1;

    let createdLesson = null;

    try {
      switch (contextItem.key) {
        case LESSON_TYPES.THEORY_LESSON: {
          createdLesson = await api.createTheoryLesson({
            courseId,
            order: nextLessonOrder,
          });

          break;
        }
        case LESSON_TYPES.FIGMA_LESSON: {
          createdLesson = await api.createFigmaLesson({
            courseId,
            order: nextLessonOrder,
          });

          break;
        }
        case LESSON_TYPES.QUIZ: {
          createdLesson = await api.createQuiz({
            courseId,
            order: nextLessonOrder,
          });

          break;
        }
        case LESSON_TYPES.CONNECTION_QUIZ: {
          createdLesson = await api.createConnectionQuiz({
            courseId,
            order: nextLessonOrder,
          });

          break;
        }
        case LESSON_TYPES.TWO_COLUMNS_LESSON: {
          createdLesson = await api.createTwoColumnsLesson({
            courseId,
            order: nextLessonOrder,
          });

          break;
        }
        case LESSON_TYPES.DIFFERENCE_LESSON: {
          createdLesson = await api.createDifferenceLesson({
            courseId,
            order: nextLessonOrder,
          });

          break;
        }
        case LESSON_TYPES.COLOR_MATCH_LESSON: {
          createdLesson = await api.createColorMatchLesson({
            courseId,
            order: nextLessonOrder,
          });

          break;
        }
      }

      if (course?.type === COURSE_TYPES.DAILY_CHALLENGE) {
        await dispatch(
          updateDailyChallengeById({
            courseId,
            data: { structure: COURSE_STRUCTURES.LESSONS },
          }),
        ).unwrap();
      } else {
        await dispatch(
          updateCourseById({
            courseId,
            data: { structure: COURSE_STRUCTURES.LESSONS },
          }),
        ).unwrap();
      }

      navigate(
        `${_getLessonBasePath(course?.type, courseId)}/${createdLesson.id}`,
      );
    } catch (error) {
      console.warn(error);
    }
  };

  return (
    <StructureSelect
      {...props}
      onAddModuleClick={handleAddModuleClick}
      onAddLessonClick={handleAddLessonClick}
    />
  );
};

export default React.memo(StructureSelectContainer) as any;
